import { useCallback, useContext } from 'react';

import { useAppDispatch } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';
import { createWorkflowHistoryThunk } from 'flow/store/redux/thunks';
import { createUiEffectContext } from 'flow/service/history';
import { changeDescription } from 'flow/store/redux/types';
import type { WorkflowStateChange } from 'flow/store/redux/types';
import { Operation } from 'flow/service/changelog/types';
import { getMatrixValueHtmlElementId } from 'flow/editor/hooks/useWorkflowHistory/helpers';
import type { Action } from 'flow/store/engine/types';
import type { MatrixFocusTarget } from 'flow/editor/hooks/useWorkflowHistory/types';

type MatrixHistoryDispatchArgs = {
  /** A callback that mutates redux state by performing its own dispatch(es). */
  stateChange: WorkflowStateChange;
  /** The history operation this change represents. Defaults to `Operation.Modify`. */
  operation?: Operation;
  /** What to re-focus on undo/redo. Passed directly to `getMatrixValueHtmlElementId`. */
  focusTarget?: MatrixFocusTarget;
};

/**
 * Centralizes matrix-model undo/redo history creation. Returns a `handleDispatch` that wraps a
 * state change in a `createWorkflowHistoryThunk`, deriving the `matrixValue` ui effect context
 * (which cell/axis to re-focus on traversal) from the caller-provided locator.
 */
export function useMatrixHistoryDispatch(actionId: Action['publicId']) {
  const dispatch = useAppDispatch();
  const { featureFlags } = useContext(UserContext);

  return useCallback(({
    stateChange,
    operation = Operation.Modify,
    focusTarget,
  }: MatrixHistoryDispatchArgs) => {
    const htmlElementId = focusTarget && getMatrixValueHtmlElementId(focusTarget);

    dispatch(createWorkflowHistoryThunk(
      stateChange,
      Boolean(featureFlags?.workflowEditorUndoRedo),
      {
        operation,
        uiEffectContext: createUiEffectContext(changeDescription.matrixValue, {
          actionId,
          htmlElementId,
        }),
      },
    ));
  }, [dispatch, featureFlags?.workflowEditorUndoRedo, actionId]);
}
